import { useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { Fragment } from 'react'
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { useAuth } from '../../contexts/AuthContext'
import { useBranding } from '../../contexts/BrandingContext'
import { SystezaLogo, Avatar } from '../ui'
import ThemeToggle from '../ui/ThemeToggle'

/**
 * SidebarLayout — shared layout for all dashboard modules.
 *
 * Props:
 *   navGroups  — [{ label, items: [{ label, to, icon, end?, badge? }] }]
 *   moduleColor — CSS color string for active state
 *   moduleTag   — string shown in sidebar header
 *   headerRight — optional JSX for top-right header area
 */
export default function SidebarLayout({ navGroups = [], moduleColor = '#1A6AF5', moduleTag = '', headerRight }) {
  const { user, logout, isAdmin } = useAuth()
  const branding   = useBranding()
  const navigate   = useNavigate()
  const [open, setOpen] = useState(false)

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const activeStyle = {
    background: `${moduleColor}14`,
    color: moduleColor,
    borderColor: `${moduleColor}30`,
  }

  const SidebarContent = () => (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Brand header */}
      <div className="flex items-center gap-3 h-16 px-5 border-b flex-shrink-0" style={{ borderColor: '#0E1E4A' }}>
        {branding.logo_url ? (
          <img src={branding.logo_url} alt={branding.brand_name} className="h-8 object-contain" />
        ) : (
          <SystezaLogo size={30} />
        )}
        <div>
          <span className="font-display font-bold text-sm text-white">{branding.brand_name}</span>
          {moduleTag && (
            <p className="font-mono text-[9px] mt-0.5 tracking-widest uppercase" style={{ color: moduleColor }}>
              {moduleTag}
            </p>
          )}
        </div>
      </div>

      {/* Module switcher */}
      <div className="px-3 pt-3 pb-1 flex-shrink-0">
        <p className="nav-group-label" style={{ marginTop: 0 }}>Modules</p>
        <div className="grid grid-cols-2 gap-1 mt-1">
          {[
            { label: 'Infra',    to: isAdmin ? '/infra/admin' : '/infra/client', color: '#1A6AF5', emoji: '🧱' },
            { label: 'Automate', to: '/automations', color: '#00C8E8', emoji: '⚙️' },
            { label: 'Engage',   to: '/engage',      color: '#00E5C8', emoji: '📈' },
            { label: 'Hub',      to: '/hub',          color: '#A855F7', emoji: '🧩' },
          ].map(m => (
            <NavLink key={m.to} to={m.to}
              className={({ isActive }) => clsx(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all',
                isActive ? 'text-white' : 'text-[var(--text-dim)] hover:text-[var(--text-muted)] hover:bg-white/4'
              )}
              style={({ isActive }) => isActive ? { background: `${m.color}18`, color: m.color } : {}}>
              <span>{m.emoji}</span>
              <span>{m.label}</span>
            </NavLink>
          ))}
        </div>
      </div>

      {/* Nav groups */}
      <nav className="flex-1 px-3 py-3 overflow-y-auto space-y-0.5">
        {navGroups.map((group, gi) => (
          <div key={gi}>
            {group.label && <p className="nav-group-label">{group.label}</p>}
            {group.items.map(item => (
              <NavLink key={item.to} to={item.to} end={item.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) => clsx('nav-link border', isActive ? 'active' : 'border-transparent')}
                style={({ isActive }) => isActive ? activeStyle : {}}>
                <span className="w-4 text-center flex-shrink-0">{item.icon}</span>
                <span className="flex-1 truncate">{item.label}</span>
                {item.badge && (
                  <span className="ml-auto text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-full"
                    style={{ background: `${moduleColor}20`, color: moduleColor }}>
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* User footer */}
      <div className="p-3 border-t flex-shrink-0" style={{ borderColor: '#0E1E4A' }}>
        {!branding.hide_systeza_branding && (
          <p className="font-mono text-[9px] text-[var(--text-dim)] text-center mb-2 tracking-widest">
            POWERED BY SYSTEZA
          </p>
        )}
        <div className="flex items-center gap-2.5 px-2 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
          <Avatar name={user?.name} size="sm" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-white truncate">{user?.name}</p>
            <p className="text-[10px] text-[var(--text-dim)] truncate capitalize">
              {user?.role?.replace('_', ' ')} · {user?.plan || 'free'}
            </p>
          </div>
          <button onClick={handleLogout} title="Sign out"
            className="text-[var(--text-dim)] hover:text-red-400 transition-colors p-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen flex" style={{ background: '#020818' }}>
      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setOpen(false)}
          style={{ background: 'rgba(0,0,0,0.6)' }} />
      )}

      {/* Mobile sidebar */}
      {open && (
        <div className="fixed inset-y-0 left-0 z-50 w-64 lg:hidden shadow-2xl" style={{ background: '#040E2A', borderRight: '1px solid #0E1E4A' }}>
          <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-[var(--text-dim)] hover:text-white">✕</button>
          <SidebarContent />
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0"
        style={{ background: '#040E2A', borderRight: '1px solid #0E1E4A' }}>
        <SidebarContent />
      </div>

      {/* Main content */}
      <div className="flex-1 lg:pl-64 flex flex-col min-h-screen">
        {/* Top header */}
        <header className="sticky top-0 z-30 flex items-center justify-between h-16 px-5 lg:px-8 border-b flex-shrink-0"
          style={{ background: 'rgba(4,14,42,0.9)', backdropFilter: 'blur(20px)', borderColor: '#0E1E4A' }}>
          <button onClick={() => setOpen(true)} className="lg:hidden btn-ghost p-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <ThemeToggle compact />
            {headerRight}
            {/* Profile quick-menu */}
            <Menu as="div" className="relative">
              <MenuButton className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-white/5 transition-colors">
                <Avatar name={user?.name} size="sm" />
                <div className="hidden sm:block text-left">
                  <p className="text-xs font-semibold text-white truncate max-w-[100px]">{user?.name}</p>
                  <p className="text-[10px] text-[var(--text-dim)] capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
                <svg className="w-3.5 h-3.5 text-[var(--text-dim)] hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </MenuButton>
              <Transition
                enter="transition ease-out duration-100" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                leave="transition ease-in duration-75" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <MenuItems className="absolute right-0 mt-2 w-52 rounded-xl shadow-lg py-1 z-50 focus:outline-none border"
                  style={{ background: '#071640', borderColor: '#1A2F60' }}>
                  <div className="px-4 py-3 border-b" style={{ borderColor: '#1A2F60' }}>
                    <p className="text-sm font-semibold text-white">{user?.name}</p>
                    <p className="text-xs text-[var(--text-dim)] truncate">{user?.email}</p>
                  </div>
                  <MenuItem>
                    {({ active }) => (
                      <button onClick={() => navigate('/profile')}
                        className={clsx('flex items-center gap-2 w-full px-4 py-2 text-sm text-[var(--text-muted)] transition-colors', active && 'bg-white/5 text-white')}>
                        👤 Profile & Settings
                      </button>
                    )}
                  </MenuItem>
                  <div className="my-1 h-px" style={{ background: '#1A2F60' }} />
                  <MenuItem>
                    {({ active }) => (
                      <button onClick={handleLogout}
                        className={clsx('flex items-center gap-2 w-full px-4 py-2 text-sm text-red-400 transition-colors', active && 'bg-red-500/10')}>
                        ↪ Sign out
                      </button>
                    )}
                  </MenuItem>
                </MenuItems>
              </Transition>
            </Menu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-5 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
